/**
 * Aurora Canvas 主页面（React Flow 版本）
 * 路径：/canvas（可选 ?id=xxx 指定画布）
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FilePlus2, FolderOpen, Home, LayoutTemplate, Maximize2, Minimize2, Plus } from 'lucide-react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  addEdge,
  type Node,
  type Edge,
  type Connection,
  type Viewport,
  type NodeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import { canvasApi } from '../utils/canvas-api'
import { useCanvasStore, type SaveStatus } from '../stores/canvas'
import { useCanvasAutosave } from '../hooks/useCanvasAutosave'
import { PromptCardNode, PROMPT_CARD_DEFAULT_DATA } from '../components/canvas/PromptCardNode'
import { ImageNode } from '../components/canvas/ImageNode'
import { RemixCardNode } from '../components/canvas/RemixCardNode'
import { ConfirmDialog } from '../components/canvas/ConfirmDialog'
import { reflowChildren } from '../components/canvas/canvas-layout'
import { CanvasTemplateSidebar } from '../components/canvas/CanvasTemplateSidebar'
import type { CanvasPromptTemplate } from '../components/canvas/canvas-templates'

const NODE_TYPES: NodeTypes = {
  'prompt-card': PromptCardNode,
  'prompt-remix': RemixCardNode,
  'image-node': ImageNode,
}

const DEFAULT_VIEWPORT: Viewport = { x: 0, y: 0, zoom: 1 }

interface CanvasSnapshot {
  nodes: Node[]
  edges: Edge[]
  viewport: Viewport
}

export function CanvasPage() {
  return (
    <ReactFlowProvider>
      <CanvasInner />
    </ReactFlowProvider>
  )
}

function CanvasInner() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const urlId = searchParams.get('id')

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const [viewport, setViewport] = useState<Viewport>(DEFAULT_VIEWPORT)
  const [bootstrapping, setBootstrapping] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [hydrated, setHydrated] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [templatePanelOpen, setTemplatePanelOpen] = useState(false)
  const rf = useReactFlow()

  const canvasId = useCanvasStore((s) => s.canvasId)
  const canvasName = useCanvasStore((s) => s.canvasName)
  const saveStatus = useCanvasStore((s) => s.saveStatus)
  const lastSavedAt = useCanvasStore((s) => s.lastSavedAt)
  const errorMessage = useCanvasStore((s) => s.errorMessage)
  const setCanvas = useCanvasStore((s) => s.setCanvas)
  const setName = useCanvasStore((s) => s.setName)

  // 持有最新 nodes/edges/viewport，用于切换画布时同步 flush
  const latestRef = useRef({ nodes, edges, viewport })
  latestRef.current = { nodes, edges, viewport }

  useCanvasAutosave({ nodes, edges, viewport, canvasId, ready: hydrated })

  // Bootstrap：根据 URL ?id= 加载指定画布；否则取最近一张；都没有则新建
  useEffect(() => {
    let cancelled = false
    setHydrated(false)
    setBootstrapping(true)
    setLoadError(null)

    ;(async () => {
      try {
        let detail
        if (urlId) {
          detail = await canvasApi.get(urlId)
        } else {
          const list = await canvasApi.list()
          if (cancelled) return
          if (list.items.length > 0) {
            detail = await canvasApi.get(list.items[0].id)
          } else {
            detail = await canvasApi.create('Untitled Canvas', {})
          }
        }
        if (cancelled) return
        setCanvas(detail.id, detail.name)

        const data = detail.data as Partial<CanvasSnapshot> | undefined
        const rawNodes = data && Array.isArray(data.nodes) ? data.nodes : []
        const rawEdges = data && Array.isArray(data.edges) ? data.edges : []
        const hydratedNodes = migrateNodes(rawNodes, rawEdges)
        setNodes(hydratedNodes)
        setEdges(rawEdges)

        const vp =
          data && data.viewport && typeof data.viewport === 'object'
            ? (data.viewport as Viewport)
            : DEFAULT_VIEWPORT
        setViewport(vp)
        queueMicrotask(() => rf.setViewport(vp))
      } catch (e) {
        console.error('[CanvasPage] bootstrap failed:', e)
        if (!cancelled) setLoadError(e instanceof Error ? e.message : '加载失败')
      } finally {
        if (!cancelled) {
          setBootstrapping(false)
          requestAnimationFrame(() => {
            if (!cancelled) setHydrated(true)
          })
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [urlId, setCanvas, setNodes, setEdges, rf])

  const handleConnect = useCallback(
    (connection: Connection) =>
      setEdges((eds) =>
        addEdge({ ...connection, animated: true, style: { stroke: '#8b5cf6', strokeWidth: 2 } }, eds),
      ),
    [setEdges],
  )

  const createPromptCardNode = useCallback((position: { x: number; y: number }, template?: CanvasPromptTemplate): Node => {
    const newId = `card_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    return {
      id: newId,
      type: 'prompt-card',
      position,
      dragHandle: '.drag-handle',
      data: template
        ? {
          ...PROMPT_CARD_DEFAULT_DATA,
          promptMode: 'pro',
          simplePrompt: '',
          fields: { ...template.fields },
          model: template.model || PROMPT_CARD_DEFAULT_DATA.model,
        }
        : { ...PROMPT_CARD_DEFAULT_DATA, fields: { ...PROMPT_CARD_DEFAULT_DATA.fields } },
    }
  }, [])

  const getViewportCenter = useCallback(() => {
    return rf.screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    })
  }, [rf])

  const addPromptCardToCurrent = useCallback(() => {
    const center = getViewportCenter()
    setNodes((ns) => [
      ...ns,
      createPromptCardNode({ x: center.x - 190, y: center.y - 180 }),
    ])
  }, [createPromptCardNode, getViewportCenter, setNodes])

  const handleApplyTemplate = useCallback((template: CanvasPromptTemplate) => {
    const center = rf.screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    })
    setNodes((ns) => [
      ...ns,
      createPromptCardNode({ x: center.x - 190, y: center.y - 260 }, template),
    ])
  }, [createPromptCardNode, rf, setNodes])

  const createStarterCard = useCallback((position = { x: 100, y: 100 }): Node => {
    return createPromptCardNode(position)
  }, [createPromptCardNode])

  const handleCreateCanvas = useCallback(async () => {
    if (bootstrapping || switching) return
    setSwitching(true)
    try {
      if (canvasId) {
        const snapshot = {
          nodes: latestRef.current.nodes,
          edges: latestRef.current.edges,
          viewport: latestRef.current.viewport,
        }
        await canvasApi.update(canvasId, { data: snapshot }).catch(() => undefined)
      }
      const newCard = createStarterCard()
      const created = await canvasApi.create('Untitled Canvas', {
        nodes: [newCard],
        edges: [],
        viewport: DEFAULT_VIEWPORT,
      })
      navigate(`/canvas?id=${encodeURIComponent(created.id)}`)
    } catch (e) {
      console.error('[CanvasPage] new canvas failed:', e)
    } finally {
      setSwitching(false)
    }
  }, [bootstrapping, switching, canvasId, navigate, createStarterCard])

  const handleRename = useCallback(async () => {
    if (!canvasId) return
    const next = window.prompt('画布名称', canvasName)
    if (!next || !next.trim() || next === canvasName) return
    setName(next)
    try {
      await canvasApi.update(canvasId, { name: next })
    } catch (e) {
      console.error('rename failed', e)
    }
  }, [canvasId, canvasName, setName])

  const handleViewportChange = useCallback((vp: Viewport) => {
    setViewport(vp)
  }, [])

  const handleOpenList = useCallback(async () => {
    // 跳转前 flush 一次
    if (canvasId) {
      try {
        await canvasApi.update(canvasId, {
          data: {
            nodes: latestRef.current.nodes,
            edges: latestRef.current.edges,
            viewport: latestRef.current.viewport,
          },
        })
      } catch {
        /* 忽略 flush 错误，依赖 unmount 兜底 */
      }
    }
    navigate('/canvases')
  }, [canvasId, navigate])

  const setAllCollapsed = useCallback(
    (collapsed: boolean) => {
      const collapsibleTypes = new Set(['prompt-card', 'prompt-remix', 'image-node'])
      let promptCardIds: string[] = []
      setNodes((ns) => {
        promptCardIds = ns.filter((n) => n.type === 'prompt-card').map((n) => n.id)
        return ns.map((n) => {
          if (!n.type || !collapsibleTypes.has(n.type)) return n
          const data = (n.data || {}) as Record<string, unknown>
          if (data.collapsed === collapsed) return n
          return { ...n, data: { ...data, collapsed } }
        })
      })
      // 让 setNodes 先 commit，再按新折叠态重排子节点（避免视觉错位）
      queueMicrotask(() => {
        for (const pid of promptCardIds) reflowChildren(rf, pid)
      })
    },
    [rf, setNodes],
  )

  const handleCollapseAll = useCallback(() => setAllCollapsed(true), [setAllCollapsed])
  const handleExpandAll = useCallback(() => setAllCollapsed(false), [setAllCollapsed])

  // 三态：all-expanded / all-collapsed / mixed；按钮根据当前态显示对应文案 + 下一步动作
  const collapseToggleState = useMemo<'all-expanded' | 'all-collapsed' | 'mixed' | 'empty'>(() => {
    const collapsibleTypes = new Set(['prompt-card', 'prompt-remix', 'image-node'])
    const targets = nodes.filter((n) => n.type && collapsibleTypes.has(n.type))
    if (targets.length === 0) return 'empty'
    let collapsedCount = 0
    for (const n of targets) {
      if ((n.data as { collapsed?: boolean } | undefined)?.collapsed) collapsedCount++
    }
    if (collapsedCount === 0) return 'all-expanded'
    if (collapsedCount === targets.length) return 'all-collapsed'
    return 'mixed'
  }, [nodes])

  const handleToggleCollapseAll = useCallback(() => {
    // 全展 → 折叠；全折 → 展开；混合 → 默认折叠（更整洁）
    if (collapseToggleState === 'all-collapsed') handleExpandAll()
    else handleCollapseAll()
  }, [collapseToggleState, handleCollapseAll, handleExpandAll])

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#020617' }}>
      <header
        style={{
          flex: '0 0 48px',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          gap: 12,
          background: '#0b1120',
          borderBottom: '1px solid #1e293b',
          color: '#e2e8f0',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <a href="/" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Home size={14} />
          返回
        </a>
        <span
          style={{
            background: 'linear-gradient(90deg,#6366f1,#8b5cf6,#06b6d4)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            letterSpacing: 0.5,
          }}
        >
          Aurora Canvas
        </span>
        <button
          type="button"
          onClick={handleRename}
          style={{
            background: 'transparent',
            border: '1px solid #334155',
            color: '#cbd5e1',
            padding: '4px 10px',
            borderRadius: 6,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          {canvasName}
        </button>

        <button
          type="button"
          onClick={addPromptCardToCurrent}
          disabled={bootstrapping || switching}
          style={{
            background: 'linear-gradient(90deg,#6366f1,#8b5cf6)',
            border: 'none',
            color: '#fff',
            padding: '6px 12px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: bootstrapping || switching ? 'not-allowed' : 'pointer',
            opacity: bootstrapping || switching ? 0.5 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Plus size={14} />
          添加卡片
        </button>

        <button
          type="button"
          onClick={() => setTemplatePanelOpen((v) => !v)}
          style={{
            background: templatePanelOpen ? 'rgba(139,92,246,0.18)' : 'transparent',
            border: `1px solid ${templatePanelOpen ? '#8b5cf6' : '#334155'}`,
            color: templatePanelOpen ? '#e9d5ff' : '#cbd5e1',
            padding: '6px 12px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <LayoutTemplate size={14} />
          模板库
        </button>

        <button
          type="button"
          onClick={handleCreateCanvas}
          disabled={bootstrapping || switching}
          style={{
            background: 'transparent',
            border: '1px solid #334155',
            color: '#cbd5e1',
            padding: '6px 12px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: bootstrapping || switching ? 'not-allowed' : 'pointer',
            opacity: bootstrapping || switching ? 0.5 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <FilePlus2 size={14} />
          {switching ? '新建中…' : '新画布'}
        </button>

        <button
          type="button"
          onClick={handleOpenList}
          style={{
            background: 'transparent',
            border: '1px solid #334155',
            color: '#cbd5e1',
            padding: '6px 12px',
            borderRadius: 6,
            fontSize: 12,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <FolderOpen size={14} />
          我的画布
        </button>

        <div style={{ flex: 1 }} />

        <CollapseToggleButton
          state={collapseToggleState}
          onClick={handleToggleCollapseAll}
        />

        <SaveIndicator status={saveStatus} lastSavedAt={lastSavedAt} errorMessage={errorMessage} />
      </header>

      <div style={{ flex: 1, position: 'relative' }}>
        {loadError ? (
          <div style={{ color: '#f87171', padding: 24, fontSize: 14 }}>加载画布失败：{loadError}</div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={handleConnect}
            onViewportChange={handleViewportChange}
            nodeTypes={NODE_TYPES}
            colorMode="dark"
            deleteKeyCode={['Backspace', 'Delete']}
            defaultViewport={DEFAULT_VIEWPORT}
            minZoom={0.1}
            maxZoom={2}
            proOptions={{ hideAttribution: false }}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#1e293b" />
            <Controls position="bottom-right" />
            <MiniMap
              position="bottom-left"
              pannable
              zoomable
              nodeColor={(n) => (n.type === 'prompt-card' ? '#8b5cf6' : '#06b6d4')}
              maskColor="rgba(2, 6, 23, 0.7)"
              style={{ background: '#0b1120', border: '1px solid #1e293b' }}
            />
          </ReactFlow>
        )}
        {!loadError ? (
          <CanvasTemplateSidebar
            open={templatePanelOpen}
            onClose={() => setTemplatePanelOpen(false)}
            onApplyTemplate={handleApplyTemplate}
          />
        ) : null}
        {!loadError && !bootstrapping && nodes.length === 0 ? (
          <EmptyCanvasState onAddCard={addPromptCardToCurrent} />
        ) : null}
      </div>

      <ConfirmDialog />
    </div>
  )
}

type CollapseState = 'all-expanded' | 'all-collapsed' | 'mixed' | 'empty'

function EmptyCanvasState({ onAddCard }: { onAddCard: () => void }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: 420,
          background: 'rgba(15,23,42,0.92)',
          border: '1px solid #334155',
          borderRadius: 12,
          padding: 22,
          boxShadow: '0 18px 60px rgba(0,0,0,0.45)',
          color: '#e2e8f0',
          fontFamily: 'Inter, system-ui, sans-serif',
          pointerEvents: 'auto',
        }}
      >
        <div style={{ color: '#a78bfa', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, marginBottom: 8 }}>
          AURORA CANVAS
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>从一张 Prompt Card 开始</div>
        <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>
          用结构化字段描述主体、构图、光照、镜头和风格。生成结果会自动挂在卡片右侧，方便继续对比和 Remix。
        </div>
        <button
          type="button"
          onClick={onAddCard}
          style={{
            background: 'linear-gradient(90deg,#6366f1,#8b5cf6,#06b6d4)',
            border: 'none',
            color: '#fff',
            padding: '10px 14px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Plus size={16} />
          添加第一张卡片
        </button>
      </div>
    </div>
  )
}

function CollapseToggleButton({ state, onClick }: { state: CollapseState; onClick: () => void }) {
  // 三态视觉：
  // - all-expanded：靛紫描边，主动作 = 折叠全部
  // - all-collapsed：青色描边，主动作 = 展开全部
  // - mixed：橙色描边 + 「混合」徽标，主动作 = 折叠全部（更整洁）
  // - empty：灰色禁用
  const config: Record<CollapseState, { label: string; mode: 'collapse' | 'expand'; border: string; color: string; tag?: string; tip: string }> = {
    'all-expanded': {
      label: '全部折叠',
      mode: 'collapse',
      border: '#8b5cf6',
      color: '#c4b5fd',
      tip: '当前全部展开，点击折叠所有卡片',
    },
    'all-collapsed': {
      label: '全部展开',
      mode: 'expand',
      border: '#06b6d4',
      color: '#67e8f9',
      tip: '当前全部折叠，点击展开所有卡片',
    },
    mixed: {
      label: '全部折叠',
      mode: 'collapse',
      border: '#f59e0b',
      color: '#fcd34d',
      tag: '混合',
      tip: '当前部分展开/部分折叠，点击折叠所有卡片',
    },
    empty: {
      label: '折叠/展开',
      mode: 'collapse',
      border: '#334155',
      color: '#475569',
      tip: '画布为空',
    },
  }
  const c = config[state]
  const disabled = state === 'empty'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={c.tip}
      style={{
        background: 'transparent',
        border: `1px solid ${c.border}`,
        color: c.color,
        padding: '4px 10px',
        borderRadius: 6,
        fontSize: 12,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {c.mode === 'expand' ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
      <span>{c.label}</span>
      {c.tag ? (
        <span
          style={{
            background: 'rgba(245,158,11,0.18)',
            border: '1px solid rgba(245,158,11,0.55)',
            color: '#fcd34d',
            fontSize: 10,
            padding: '0 4px',
            borderRadius: 4,
            lineHeight: '14px',
          }}
        >
          {c.tag}
        </span>
      ) : null}
    </button>
  )
}

function SaveIndicator({ status, lastSavedAt, errorMessage }: { status: SaveStatus; lastSavedAt: number | null; errorMessage: string | null }) {
  const colorMap: Record<SaveStatus, string> = {
    idle: '#64748b',
    saving: '#fbbf24',
    saved: '#34d399',
    error: '#f87171',
  }
  const labelMap: Record<SaveStatus, string> = {
    idle: '就绪',
    saving: '保存中…',
    saved: lastSavedAt ? `已保存 ${formatTime(lastSavedAt)}` : '已保存',
    error: '保存失败',
  }
  return (
    <span title={status === 'error' ? errorMessage || '保存失败' : undefined} style={{ color: colorMap[status], fontSize: 12, fontFamily: 'JetBrains Mono, monospace' }}>
      ● {labelMap[status]}
    </span>
  )
}

function formatTime(ts: number): string {
  const d = new Date(ts)
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

/**
 * 旧画布迁移：
 *  - 回填 dragHandle
 *  - 把根据 edge.source 找到 PromptCard 父的 ImageNode 挂为 parentId 子节点
 *  - 子节点 position 由绝对坐标换算为相对父节点坐标
 *  - 父节点排在子节点之前（React Flow 强制要求）
 */
function migrateNodes(nodes: Node[], edges: Edge[]): Node[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const childToParent = new Map<string, string>()
  for (const e of edges) {
    const src = byId.get(e.source)
    const tgt = byId.get(e.target)
    if (!src || !tgt) continue
    if (src.type === 'prompt-card' && tgt.type === 'image-node' && !childToParent.has(tgt.id)) {
      childToParent.set(tgt.id, src.id)
    }
  }

  const migrated = nodes.map((n) => {
    const base =
      n.type === 'prompt-card' || n.type === 'prompt-remix' || n.type === 'image-node'
        ? { ...n, dragHandle: '.drag-handle' }
        : n
    if (base.type !== 'image-node') return base
    if (base.parentId) return base

    const parentId = childToParent.get(base.id)
    if (!parentId) return base
    const parent = byId.get(parentId)
    if (!parent) return base

    return {
      ...base,
      parentId,
      position: {
        x: base.position.x - parent.position.x,
        y: base.position.y - parent.position.y,
      },
    }
  })

  return migrated.sort((a, b) => {
    if (a.parentId === b.id) return 1
    if (b.parentId === a.id) return -1
    return 0
  })
}
